<script setup lang="ts">
import { ref, onMounted, watch, computed, nextTick } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useOfficerCessationStore } from '../stores/officerCessationStore';
// getOfficersByEntity는 더 이상 사용하지 않음 (loadAllOfficers로 변경)
import { useDateField, formatDocDate } from '@/shared/composables/dateUtils';
import { formatDateByJurisdiction } from '@/features/register/utils/dateFormat';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import { entityApi } from '@/features/Entity/api/entityApi';
import { useAddressFormatter } from '@/shared/composables/useAddressFormatter';
import { useEntityDocVariables } from '@/shared/composables/useEntityDocVariables';
import TaskFlowSection from './TaskFlowSection.vue';
import type { TaskConfig } from '../api/taskConfigApi';

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// 주소 포맷팅 함수
const { formatAddress } = useAddressFormatter();

// Entity doc_variables 조회
const { getEntityData } = useEntityDocVariables();

// Props: CreateTaskForm에서 선택한 taskConfig 전달받기
interface Props {
  taskConfig?: TaskConfig | null;
  initialData?: any | null; // E-Filing 페이지에서 TASK_DATA 전달용
  readOnly?: boolean; // 읽기 전용 모드 (E-Filing 검증용)
  showTaskFlow?: boolean; // Task Flow 섹션 표시 여부 (E-Filing에서는 false)
  eFilingMode?: boolean; // E-Filing 모드
}

const props = withDefaults(defineProps<Props>(), {
  taskConfig: null,
  initialData: null,
  readOnly: false,
  showTaskFlow: true,
  eFilingMode: false
});

// Store
const officerCessationStore = useOfficerCessationStore();
const sessionStore = useSessionStore();

// Emits
const emit = defineEmits<{
  'auto-select-templates': [config: { categoryName: string; templateIds: string[] }],
  'filter-select-templates': [config: { categoryName: string; templateIds: string[]; filterValue: string }],
  'reset-templates': [],
  'update:taskFlow': [value: { all: boolean; createDocument: boolean; requestTSign: boolean; proceedWithEFiling: boolean }]
}>();

const entity = ref('');
const officerName = ref('');
const position = ref(''); // Position 선택 가능
const reasonForCessation = ref(''); // B12 공통코드 값 (예: 'B12001', 'B12004')
const otherReason = ref(''); // B12004 선택 시 입력할 텍스트

// Task Flow 상태
const taskFlow = ref({
  all: false,
  createDocument: false,
  requestTSign: false,
  proceedWithEFiling: false
});

// Officer 목록 (Entity 선택 시 동적으로 로드)
// 각 Officer는 여러 Position을 가질 수 있음
interface OfficerPosition {
  positionCd: string;
  positionName: string;
  appointmentDate: string;
}
interface Officer {
  contactCd: string;
  contactName: string;
  contactRegNo?: string; // ID/Registration Number
  idTypeCd?: string; // ID_TYPE_CD (B03 코드)
  positions: OfficerPosition[];
}
const officers = ref<Officer[]>([]);
const isLoadingOfficers = ref(false);
const selectedOfficerIndex = ref<number | null>(null); // 선택된 Officer 인덱스
const expandedPanels = ref<number[]>([]); // 확장된 패널 인덱스
const officerFormMode = ref<Map<number, boolean>>(new Map()); // 각 Officer별 폼 표시 모드 (Withdraw 클릭 시 true)
const withdrawnOfficerIndex = ref<number | null>(null); // WITHDRAW로 선택된 Officer 인덱스 (단건만 허용)
const officerContactFullInfo = ref<Map<number, any>>(new Map()); // 각 Officer별 전체 Contact 정보 (L_GLOBAL_CONTACT)
const officerDateOfCessation = ref<Map<number, string>>(new Map()); // 각 Officer별 해임 날짜
const officerReasonForCessation = ref<Map<number, string>>(new Map()); // 각 Officer별 해임 사유
const officerOtherReason = ref<Map<number, string>>(new Map()); // 각 Officer별 기타 사유
const officerSelectedPosition = ref<Map<number, string>>(new Map()); // 각 Officer별 선택된 직책 (positionCd)
const officerDateMenuOpen = ref<Map<number, boolean>>(new Map()); // 각 Officer별 날짜 메뉴 열림 상태

// B03 ID_TYPE_CD 코드 NAME 매핑
const idTypeCodeNameMap = ref<Map<string, string>>(new Map());

// Cessation Reason 옵션 (B12 공통코드)
const reasonOptions = ref<Array<{ title: string; value: string }>>([]);
const isLoadingReasons = ref(false);

// Officer 옵션은 더 이상 필요 없음 (Accordion으로 변경)

// 내부 validation 상태 관리
const internalValidationErrors = ref<Record<string, string>>({});

// 필드 refs (포커스 이동용)
const entityInputRef = ref<any>(null);

// Entity 이름 (entityOptions에서 찾기)
const entityName = computed(() => {
  // E-Filing 모드에서는 initialData에서 entity 이름 가져오기
  if (props.eFilingMode && props.initialData?.formData?.entity) {
    const entityCd = props.initialData.formData.entity;
    const found = officerCessationStore.entityOptions.find(opt => opt.value === entityCd);
    if (found) return found.title;
    // 또는 initialData에서 직접 entityName 가져오기
    return props.initialData.formData.entityName || props.initialData.entityName || '';
  }
  // 일반 모드
  if (!entity.value) return '';
  const found = officerCessationStore.entityOptions.find(opt => opt.value === entity.value);
  return found?.title || '';
});

// Entity REG_NO (entities에서 찾기)
const entityRegNo = computed(() => {
  if (!entity.value) return '';
  const found = officerCessationStore.entities.find(e => 
    (e.entityCd || e.ENTITY_CD) === entity.value
  );
  return found?.regNo || found?.REG_NO || '';
});

// Officer 주소 정보
const officerAddressInfo = ref<{
  ADDRESS_TYPE_CD?: string;
  POSTAL_CODE?: string;
  BLOCK_NO?: string;
  STREET_NAME?: string;
  BUILDING_NAME?: string;
  ADDR_LEVEL?: string;
  ADDR_UNIT?: string;
  FOREIGN_ADDRESS_LINE_1?: string;
  FOREIGN_ADDRESS_LINE_2?: string;
}>({});

// Entity 주소 정보
const entityAddressInfo = ref<{
  ADDRESS_TYPE_CD?: string;
  POSTAL_CODE?: string;
  BLOCK_NO?: string;
  STREET_NAME?: string;
  BUILDING_NAME?: string;
  ADDR_LEVEL?: string;
  ADDR_UNIT?: string;
  FOREIGN_ADDRESS_LINE_1?: string;
  FOREIGN_ADDRESS_LINE_2?: string;
  JURISDICTION?: string;
}>({});

// Officer 실제 이름 (officers에서 찾기)
const officerDisplayName = computed(() => {
  if (selectedOfficerIndex.value === null || selectedOfficerIndex.value < 0 || selectedOfficerIndex.value >= officers.value.length) return '';
  return officers.value[selectedOfficerIndex.value]?.contactName || '';
});

// 날짜 필드 (register 폴더와 동일한 방식)
// APPOINTMENT_DATE: readonly (L_OFFICER에서 가져온 값)
const appointedDateField = useDateField(null);

// CESSATION_DATE: 선택 가능
const dateOfCessationField = useDateField(null);

// Jurisdiction 기반 포맷으로 dateDisplay 덮어쓰기 (register 폴더와 동일)
const appointedDateDisplay = computed(() => formatDateByJurisdiction(appointedDateField.dateValue.value));
const dateOfCessationDisplay = computed(() => formatDateByJurisdiction(dateOfCessationField.dateValue.value));

// Jurisdiction 기반 placeholder (formatDateByJurisdiction 로직과 동일)
const datePlaceholderByJurisdiction = computed(() => {
  const jurisdiction = sessionStore.user?.customer?.jurisdiction;
  let formatToUse = 'YYYY-MM-DD';
  
  if (jurisdiction) {
    const jurisdictionFormatMap: Record<string, string> = {
      'SG': 'DD/MM/YYYY',  // 싱가포르
      'MY': 'DD/MM/YYYY',  // 말레이시아
      'VN': 'DD/MM/YYYY',  // 베트남
      'US': 'MM/DD/YYYY',  // 미국
      'CN': 'YYYY-MM-DD',  // 중국
      'HK': 'DD/MM/YYYY',  // 홍콩
      'TW': 'YYYY/MM/DD',  // 대만
      'KR': 'YYYY-MM-DD',  // 한국
      'JP': 'YYYY/MM/DD',  // 일본
      'KP': 'YYYY-MM-DD',  // 북한
      'TH': 'DD/MM/YYYY',  // 태국
      'ID': 'DD/MM/YYYY',  // 인도네시아
      'PH': 'MM/DD/YYYY',  // 필리핀
      'IN': 'DD/MM/YYYY',  // 인도
      'AU': 'DD/MM/YYYY',  // 호주
      'GB': 'DD/MM/YYYY',  // 영국
    };
    const jurisdictionFormat = jurisdictionFormatMap[jurisdiction.toUpperCase()];
    if (jurisdictionFormat) {
      formatToUse = jurisdictionFormat;
    }
  }
  
  // Jurisdiction 포맷이 없으면 사용자 dateFormat 사용
  if (formatToUse === 'YYYY-MM-DD' && sessionStore.user?.user?.dateFormat) {
    formatToUse = sessionStore.user.user.dateFormat.toUpperCase();
  }
  
  return formatToUse;
});

// v-menu의 v-model을 위한 computed
const dateOfCessationMenu = computed({
  get: () => dateOfCessationField.dateMenu.value,
  set: (val: boolean) => { dateOfCessationField.dateMenu.value = val; }
});

// 오늘 날짜 (미래 날짜 선택 방지용)
const todayDate = computed(() => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
});

// Position 옵션은 더 이상 필요 없음 (Accordion에서 Officer 선택 시 자동 설정)
// 하지만 기존 로직 유지를 위해 computed 유지
const positionOptions = computed(() => {
  const storeOptions = officerCessationStore.positionOptions || [];
  // 허용된 Position 코드만 필터링
  const allowedPositions = ['B30033', 'B30025', 'B30027', 'B30044'];
  const filtered = storeOptions.filter(opt => allowedPositions.includes(opt.value));
  
  // B30044 (Nominee Director)가 없으면 명시적으로 추가
  const hasNomineeDirector = filtered.some(opt => opt.value === 'B30044');
  if (!hasNomineeDirector) {
    filtered.push({
      title: 'Nominee Director',
      value: 'B30044'
    });
  }
  
  return filtered;
});

// B12004 선택 여부 확인
const showOtherReason = computed(() => reasonForCessation.value === 'B12004');

// E-Filing 모드: 해임 대상 정보 (initialData에서 가져오기)
const withdrawnOfficerName = computed(() => {
  if (!props.eFilingMode || !props.initialData?.formData) return '';
  const formData = props.initialData.formData;
  
  // officerName이 객체인 경우 처리 (doc_variable 구조)
  if (formData.officerName && typeof formData.officerName === 'object') {
    return formData.officerName.value || '';
  }
  
  // officerDisplayName이 객체인 경우 처리
  if (formData.officerDisplayName && typeof formData.officerDisplayName === 'object') {
    return formData.officerDisplayName.value || '';
  }
  
  // 문자열인 경우 그대로 반환
  if (typeof formData.officerName === 'string') {
    return formData.officerName;
  }
  
  if (typeof formData.officerDisplayName === 'string') {
    return formData.officerDisplayName;
  }
  
  // officerNameCd로 officers에서 찾기 (Entity가 로드된 경우)
  if (formData.officerNameCd && officers.value.length > 0) {
    const officer = officers.value.find(o => o.contactCd === formData.officerNameCd);
    if (officer) return officer.contactName;
  }
  
  return '';
});

// Position 이름을 저장할 ref (E-Filing 모드용)
const withdrawnPositionNameRef = ref<string>('');

// E-Filing 모드에서 Position 코드를 이름으로 변환
const loadPositionName = async (positionCd: string) => {
  if (!positionCd) {
    withdrawnPositionNameRef.value = '';
    return;
  }

  // 먼저 positionOptions에서 찾기
  const options = positionOptions.value || [];
  if (Array.isArray(options) && options.length > 0) {
    const position = options.find((opt: any) => opt.value === positionCd);
    if (position?.title) {
      withdrawnPositionNameRef.value = position.title;
      return;
    }
  }

  // positionOptions에 없으면 API로 조회 (B30 공통코드)
  try {
    const response = await baseCodeApi.getCodeByTotalCode('CM', positionCd);
    const codeName = (response as any)?.code?.codeName || 
                     (response as any)?.code?.CODE_NAME || 
                     (response as any)?.data?.code?.codeName ||
                     (response as any)?.data?.code?.CODE_NAME || '';
    withdrawnPositionNameRef.value = codeName || positionCd;
  } catch (error) {
    console.error('Error loading position name:', error);
    withdrawnPositionNameRef.value = positionCd;
  }
};

const withdrawnPositionName = computed(() => {
  if (!props.eFilingMode || !props.initialData?.formData) return '';
  const formData = props.initialData.formData;
  const positionCd = formData.position;
  if (!positionCd) return '';
  
  // positionOptions에서 먼저 찾기
  try {
    const options = positionOptions.value || [];
    if (Array.isArray(options) && options.length > 0) {
      const position = options.find((opt: any) => opt.value === positionCd);
      if (position?.title) {
        return position.title;
      }
    }
  } catch (error) {
    console.error('Error finding position name:', error);
  }
  
  // positionOptions에 없으면 ref에서 가져오기 (API 호출로 로드됨)
  return withdrawnPositionNameRef.value || positionCd;
});


const withdrawnDateOfCessationDisplay = computed(() => {
  if (!props.eFilingMode || !props.initialData?.formData) return '';
  const formData = props.initialData.formData;
  const dateVal = formData.dateOfCessation?.value || formData.dateOfCessation;
  if (!dateVal) return '';
  return formatDateByJurisdiction(dateVal);
});

const withdrawnReasonDisplay = computed(() => {
  if (!props.eFilingMode || !props.initialData?.formData) return '';
  const formData = props.initialData.formData;
  const reasonCd = formData.reasonForCessation;
  if (!reasonCd) return '';
  // reasonOptions에서 이름 찾기
  try {
    const options = reasonOptions.value || [];
    if (Array.isArray(options) && options.length > 0) {
      const reason = options.find((opt: any) => opt.value === reasonCd);
      return reason?.title || reasonCd;
    }
  } catch (error) {
    console.error('Error finding reason name:', error);
  }
  return reasonCd;
});

const withdrawnReasonCd = computed(() => {
  if (!props.eFilingMode || !props.initialData?.formData) return '';
  const formData = props.initialData.formData;
  return formData.reasonForCessation || '';
});

const withdrawnOtherReason = computed(() => {
  if (!props.eFilingMode || !props.initialData?.formData) return '';
  const formData = props.initialData.formData;
  return formData.otherReason || '';
});

// B03 ID_TYPE_CD 코드 NAME 로드
const loadIdTypeCodeNames = async () => {
  try {
    const response = await baseCodeApi.getCodesByMainCode('CM', 'B03', undefined);
    const codes = (response as any).codes || response.data?.codes || [];
    const map = new Map<string, string>();
    codes.forEach((code: any) => {
      const totalCode = String(code.totalCode || code.TOTAL_CODE || '').trim();
      const codeName = String(code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '').trim();
      if (totalCode && codeName && !map.has(totalCode)) {
        map.set(totalCode, codeName);
      }
    });
    idTypeCodeNameMap.value = map;
    console.log('✅ B03 ID_TYPE_CD 코드 NAME 로드 완료:', map);
  } catch (error: any) {
    console.error('❌ Failed to load ID_TYPE_CD code names:', error);
    idTypeCodeNameMap.value = new Map();
  }
};

// B03 ID_TYPE_CD 코드 NAME 조회 헬퍼 함수
const getIdTypeCodeName = (code: string): string => {
  if (!code) return '';
  return idTypeCodeNameMap.value.get(code) || code;
};

// Task Flow 지원 여부 (PROCESS_STEPS 기반) - 대소문자 구분 없이 비교
const isCreateDocumentSupported = computed(() => {
  const config = props.taskConfig;
  if (!config || !config.processSteps || !Array.isArray(config.processSteps)) {
    return false;
  }
  return config.processSteps.some(step => {
    const lowerStep = step.toLowerCase();
    return lowerStep === 'doc' || lowerStep === 'document' || lowerStep.includes('doc');
  });
});

const isRequestTSignSupported = computed(() => {
  const config = props.taskConfig;
  if (!config || !config.processSteps || !Array.isArray(config.processSteps)) {
    return false;
  }
  return config.processSteps.some(step => {
    const lowerStep = step.toLowerCase();
    return lowerStep === 't-sign' || lowerStep === 'tsign' || lowerStep.includes('sign');
  });
});

const isProceedWithEFilingSupported = computed(() => {
  const config = props.taskConfig;
  if (!config || !config.processSteps || !Array.isArray(config.processSteps)) {
    return false;
  }
  return config.processSteps.some(step => {
    const lowerStep = step.toLowerCase();
    return lowerStep === 'e-filing' || lowerStep.includes('filing');
  });
});

// Director, Secretary인 경우에만 E-Filing 활성화
const isEFilingEnabledForPosition = computed(() => {
  const positionCode = position.value;
  if (!positionCode) return false;

  // positionOptions에서 선택된 position 찾기
  const selectedPosition = positionOptions.value.find(opt => opt.value === positionCode);
  const positionName = selectedPosition?.title?.toLowerCase() || '';

  // Director 또는 Secretary인 경우에만 E-Filing 활성화
  return positionName.includes('director') || positionName.includes('secretary');
});

// 실제 E-Filing 지원 여부 (processSteps 지원 + Position이 Director/Secretary)
const isEFilingSupportedFinal = computed(() => {
  return isProceedWithEFilingSupported.value && isEFilingEnabledForPosition.value;
});

// 실제 All 지원 여부
const isAllSupportedFinal = computed(() => {
  return isCreateDocumentSupported.value &&
         isRequestTSignSupported.value &&
         isEFilingSupportedFinal.value;
});

// Task Flow 변경 시 상위 컴포넌트에 전달
watch(taskFlow, (newValue) => {
  emit('update:taskFlow', { ...newValue });
}, { deep: true });

// Task Flow 상태 업데이트 함수
const updateTaskFlowState = () => {
  taskFlow.value.createDocument = isCreateDocumentSupported.value;
  taskFlow.value.requestTSign = isRequestTSignSupported.value;
  taskFlow.value.proceedWithEFiling = isEFilingSupportedFinal.value;
  taskFlow.value.all = isAllSupportedFinal.value;
};

// taskConfig 변경 시 Task Flow 자동 업데이트
watch(() => props.taskConfig, () => {
  updateTaskFlowState();
}, { immediate: true });

// OfficerCessationForm은 화면 진입 시 자동 문서 선택하지 않음
// Position 선택 시에만 문서가 자동 선택됨 (updateOfficerSelectedPosition에서 처리)

// OfficerCessationForm은 Position 선택 후에만 문서가 자동 선택되어야 하므로
// taskConfig 변경 시 자동 선택하지 않음 (autoSelectCategoryAndFiles 호출 제거)
// Position 선택 시 updateOfficerSelectedPosition에서 filter-select-templates를 emit함

// B12 공통코드 목록 조회 (register 폴더와 동일한 방식)
const fetchCessationReasons = async () => {
  try {
    isLoadingReasons.value = true;
    // 'SG' 국가 코드 사용
    const response = await baseCodeApi.getCodesByMainCode('SG', 'B12', undefined);
    
    // register 폴더와 동일한 응답 구조 처리
    const codes = (response as any).codes || response.data?.codes || [];
    
    // TOTAL_CODE별로 중복 제거 (같은 TOTAL_CODE가 여러 언어로 있을 수 있음)
    // SUB_CODE가 '000'인 것은 제외
    const codeMap = new Map<string, { title: string; value: string }>();
    
    codes.forEach((code: any) => {
      const totalCode = code.totalCode || code.TOTAL_CODE || '';
      const subCode = code.subCode || code.SUB_CODE || '';
      
      // SUB_CODE가 '000'인 것은 제외
      if (subCode === '000') {
        return;
      }
      
      if (totalCode && !codeMap.has(totalCode)) {
        codeMap.set(totalCode, {
          title: code.codeName || code.CODE_NAME || code.defaultCodeName || code.DEFAULT_CODE_NAME || '',
          value: totalCode
        });
      }
    });
    
    reasonOptions.value = Array.from(codeMap.values());
    console.log('✅ Cessation reasons loaded:', reasonOptions.value);
  } catch (error: any) {
    console.error('❌ Failed to fetch cessation reasons:', error);
    reasonOptions.value = [];
  } finally {
    isLoadingReasons.value = false;
  }
};

// Officer 목록 조회 함수 (Entity별 모든 Officer 조회)
const loadAllOfficers = async (entityCd: string) => {
  if (!entityCd || !entityCd.trim()) {
    officers.value = [];
    return;
  }

  try {
    isLoadingOfficers.value = true;
    const { apiClient } = await import('@/shared/api/client');
    const response = await apiClient.get(`/startTask/common/officers-by-entity-all/${entityCd}`);
    
    if (response.success && response.data) {
      const officerList = response.data.officers || [];
      
      // 각 Officer의 Contact 정보 조회하여 contactRegNo, nationalityCd 추가
      const officersWithDetails = await Promise.all(
        officerList.map(async (officer: any) => {
          try {
            const contactResponse = await contactsApi.getContactById(officer.contactCd);
            const contact = (contactResponse as any).contact || contactResponse.data?.contact;
            return {
              contactCd: officer.contactCd || '',
              contactName: officer.contactName || '',
              contactRegNo: contact?.CONTACT_REG_NO || contact?.contactRegNo || '',
              idTypeCd: contact?.ID_TYPE_CD || contact?.idTypeCd || '',
              positions: officer.positions || []
            };
          } catch (error) {
            console.warn('Failed to fetch contact details for', officer.contactCd, error);
            return {
              contactCd: officer.contactCd || '',
              contactName: officer.contactName || '',
              contactRegNo: '',
              idTypeCd: '',
              positions: officer.positions || []
            };
          }
        })
      );
      
      officers.value = officersWithDetails;
      console.log('✅ Officer 목록 조회 성공:', officers.value);
    } else {
      console.warn('⚠️ Officer 목록 조회 실패:', (response as any).error);
      officers.value = [];
    }
  } catch (error: any) {
    console.error('❌ Failed to fetch officers:', error);
    officers.value = [];
  } finally {
    isLoadingOfficers.value = false;
  }
};

// Entity 변경 시 모든 Officer 목록 조회 및 Entity 주소 정보 조회
watch(entity, async (newEntityCd) => {
  // Entity 변경 시 validation 에러 제거
  if (internalValidationErrors.value.entity) {
    delete internalValidationErrors.value.entity;
  }

  // E-Filing 모드일 때는 Officer 목록 로드하지 않음
  if (props.eFilingMode) {
    return;
  }

  // Entity가 선택되지 않았거나 변경된 경우 초기화
  officerName.value = '';
  officers.value = [];
  selectedOfficerIndex.value = null;
  expandedPanels.value = [];
  appointedDateField.dateValue.value = '';
  dateOfCessationField.dateValue.value = '';
  position.value = '';
  entityAddressInfo.value = {};

  if (!newEntityCd || !newEntityCd.trim()) {
    return;
  }

  // Entity가 선택되면 Position 공통코드 로드
  try {
    await officerCessationStore.fetchPositions(newEntityCd);
  } catch (error) {
    console.error('Failed to fetch positions:', error);
  }

  // Entity가 선택되면 모든 Officer 목록 조회
  await loadAllOfficers(newEntityCd);

  // Entity 주소 정보 조회
  try {
    const response = await entityApi.getEntityProfile(newEntityCd);
    const entityData = (response as any).data || response;
    if (entityData) {
      // Entity 주소 정보는 profile 데이터에서 가져오기
      // registeredAddress와 alternateAddress 구조 사용
      const registeredAddress = entityData.registeredAddress || {};
      const alternateAddress = entityData.alternateAddress || {};
      
      // ADDRESS_TYPE_CD는 Entity에 없으므로 registeredAddress가 있으면 'B07001', alternateAddress만 있으면 'B07002'로 추론
      let addressTypeCd = '';
      if (registeredAddress && (registeredAddress.postalCode || registeredAddress.streetName)) {
        addressTypeCd = 'B07001'; // Local Address
      } else if (alternateAddress && (alternateAddress.foreignAddressLine1 || alternateAddress.line1)) {
        addressTypeCd = 'B07002'; // Foreign Address
      }
      
      entityAddressInfo.value = {
        ADDRESS_TYPE_CD: addressTypeCd,
        POSTAL_CODE: registeredAddress.postalCode || '',
        BLOCK_NO: registeredAddress.blockHouseNo || '',
        STREET_NAME: registeredAddress.streetName || '',
        BUILDING_NAME: registeredAddress.buildingEstateName || registeredAddress.buildingName || '',
        ADDR_LEVEL: registeredAddress.level || '',
        ADDR_UNIT: registeredAddress.unit || '',
        FOREIGN_ADDRESS_LINE_1: alternateAddress.foreignAddressLine1 || alternateAddress.line1 || '',
        FOREIGN_ADDRESS_LINE_2: alternateAddress.foreignAddressLine2 || alternateAddress.line2 || '',
        JURISDICTION: entityData.jurisdiction || entityData.JURISDICTION || ''
      };
      console.log('✅ Entity 주소 정보 조회 완료:', entityAddressInfo.value);
    }
  } catch (error: any) {
    console.error('❌ Failed to fetch entity address info:', error);
    entityAddressInfo.value = {};
  }
});

// Position 변경 시 처리 (더 이상 필요 없지만 기존 로직 유지)
watch(position, async (newPositionCd) => {
  // Position 선택은 더 이상 필요하지 않지만 기존 로직 유지
  // Accordion에서 Officer 선택 시 해당 Officer의 첫 번째 Position 사용
});

// reasonForCessation 변경 시 otherReason 초기화
watch(reasonForCessation, (newValue) => {
  if (newValue !== 'B12004') {
    otherReason.value = '';
    // 선택된 Officer의 otherReason도 초기화
    if (selectedOfficerIndex.value !== null) {
      officerOtherReason.value.delete(selectedOfficerIndex.value);
    }
  }
});

// Officer별 날짜 값을 Date 객체로 변환하는 헬퍼 함수
const getOfficerDateValue = (index: number): Date | null => {
  const dateStr = officerDateOfCessation.value.get(index) as string | undefined;
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date;
    }
  } catch {
    // 날짜 파싱 실패
  }
  return null;
};

// Officer별 데이터 업데이트 헬퍼 함수들
const updateOfficerDateOfCessation = (index: number, val: Date | string | null) => {
  selectOfficer(index);
  let dateStr = '';
  if (val) {
    if (val instanceof Date) {
      // Date 객체인 경우 YYYY-MM-DD 형식으로 변환
      const year = val.getFullYear();
      const month = String(val.getMonth() + 1).padStart(2, '0');
      const day = String(val.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    } else if (typeof val === 'string') {
      dateStr = val;
    }
  }
  officerDateOfCessation.value.set(index, dateStr);
  officerDateMenuOpen.value.set(index, false); // 날짜 선택 시 메뉴 닫기
  
  // Date of Cessation 변경 시 validation 에러 제거
  if (withdrawnOfficerIndex.value === index && internalValidationErrors.value.dateOfCessation) {
    delete internalValidationErrors.value.dateOfCessation;
  }
  const currentIdx = selectedOfficerIndex.value;
  if (currentIdx === index) {
    dateOfCessationField.dateValue.value = dateStr;
    if (dateStr) {
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          dateOfCessationField.datePickerValue.value = date;
        }
      } catch {
        // 날짜 파싱 실패 시 무시
      }
    } else {
      dateOfCessationField.datePickerValue.value = null;
    }
  }
};

const updateOfficerReasonForCessation = (index: number, val: string) => {
  selectOfficer(index);
  officerReasonForCessation.value.set(index, val || '');
  const currentIdx = selectedOfficerIndex.value;
  if (currentIdx === index) {
    reasonForCessation.value = val || '';
  }
  if (val !== 'B12004') {
    officerOtherReason.value.delete(index);
  }
  
  // Reason for Cessation 변경 시 validation 에러 제거
  if (withdrawnOfficerIndex.value === index && internalValidationErrors.value.reasonForCessation) {
    delete internalValidationErrors.value.reasonForCessation;
  }
};

const updateOfficerOtherReason = (index: number, val: string) => {
  selectOfficer(index);
  officerOtherReason.value.set(index, val || '');
  const currentIdx = selectedOfficerIndex.value;
  if (currentIdx === index) {
    otherReason.value = val || '';
  }
};

// Officer별 선택된 직책 업데이트 헬퍼 함수
const updateOfficerSelectedPosition = (index: number, val: string) => {
  officerSelectedPosition.value.set(index, val || '');
  selectOfficer(index);
  const currentIdx = selectedOfficerIndex.value;
  if (currentIdx !== null && currentIdx === index) {
    position.value = val || '';
  }
  
  // Position 변경 시 validation 에러 제거
  if (internalValidationErrors.value.position) {
    delete internalValidationErrors.value.position;
  }
  
  // Position 변경 시 Task Flow 상태 업데이트 (E-Filing 활성화/비활성화)
  updateTaskFlowState();
  
  // Position 변경 시 문서 자동 선택
  if (val && val.trim()) {
    // taskConfig.documents에서 filter로 필터링하여 emit
    const taskConfig = props.taskConfig;
    if (taskConfig && taskConfig.documents && Array.isArray(taskConfig.documents)) {
      // 해당 Position(filter)에 맞는 문서만 필터링
      const filteredDocuments = taskConfig.documents.filter(
        doc => doc.filter === val
      );

      if (filteredDocuments.length > 0) {
        const templateIds = filteredDocuments.map(doc => doc.DOC_TYPE).filter(id => id);
        const categoryName = filteredDocuments[0]?.category?.trim() || null;

        console.log('📋 [OfficerCessationForm] Filter로 문서 필터링:', {
          filterValue: val,
          totalDocuments: taskConfig.documents.length,
          filteredCount: filteredDocuments.length,
          templateIds,
          categoryName
        });

        // filter로 필터링한 문서 목록 emit
        emit('filter-select-templates', {
          categoryName: categoryName || '',
          templateIds,
          filterValue: val
        });
      } else {
        // 해당 filter에 맞는 문서가 없으면 초기화
        console.log('📋 [OfficerCessationForm] 해당 Filter의 문서 없음 - 초기화:', {
          filterValue: val
        });
        emit('reset-templates');
      }
    }
  } else {
    // Position이 선택 해제되면 템플릿 초기화
    emit('reset-templates');
  }
};

// dateOfCessation 변경 시 해당 Officer의 데이터 동기화
watch(() => dateOfCessationField.dateValue.value, (newValue) => {
  const currentIdx = selectedOfficerIndex.value;
  if (currentIdx !== null) {
    officerDateOfCessation.value.set(currentIdx, newValue);
  }
});

// reasonForCessation 변경 시 해당 Officer의 데이터 동기화
watch(reasonForCessation, (newValue) => {
  const currentIdx = selectedOfficerIndex.value;
  if (currentIdx !== null) {
    officerReasonForCessation.value.set(currentIdx, newValue);
  }
});

// otherReason 변경 시 해당 Officer의 데이터 동기화
watch(otherReason, (newValue) => {
  const currentIdx = selectedOfficerIndex.value;
  if (currentIdx !== null) {
    if (newValue) {
      officerOtherReason.value.set(currentIdx, newValue);
    } else {
      officerOtherReason.value.delete(currentIdx);
    }
  }
});

// Withdraw 버튼 클릭 시 해당 Officer의 폼 모드 활성화 (단건만 허용)
const handleWithdraw = async (index: number) => {
  if (index < 0 || index >= officers.value.length) {
    return;
  }
  
  // 이미 다른 Officer가 WITHDRAW 모드인 경우, 해당 Officer의 모드 해제
  if (withdrawnOfficerIndex.value !== null && withdrawnOfficerIndex.value !== index) {
    const prevIndex = withdrawnOfficerIndex.value;
    officerFormMode.value.set(prevIndex, false);
    officerDateOfCessation.value.delete(prevIndex);
    officerReasonForCessation.value.delete(prevIndex);
    officerOtherReason.value.delete(prevIndex);
    officerSelectedPosition.value.delete(prevIndex);
    officerDateMenuOpen.value.set(prevIndex, false);
  }
  
  const officer = officers.value[index];
  
  // 해당 Officer의 전체 Contact 정보 조회
  try {
    const contactResponse = await contactsApi.getContactById(officer.contactCd);
    const contact = (contactResponse as any).contact || contactResponse.data?.contact;
    if (contact) {
      officerContactFullInfo.value.set(index, contact);
      console.log('✅ Officer Contact 정보 로드 완료:', contact);
    }
  } catch (error) {
    console.error('❌ Failed to fetch officer contact info:', error);
  }
  
  // WITHDRAW 모드 활성화
  withdrawnOfficerIndex.value = index;
  officerFormMode.value.set(index, true);
  
  // 패널 확장
  if (!expandedPanels.value.includes(index)) {
    expandedPanels.value.push(index);
  }
  
  // 해당 Officer 선택 (폼 데이터 동기화)
  await selectOfficer(index);
  
  // 직책이 하나면 자동 선택 (문서 자동 선택 트리거를 위해 updateOfficerSelectedPosition 호출)
  if (officer.positions && officer.positions.length === 1) {
    const positionCd = officer.positions[0].positionCd || '';
    if (positionCd) {
      updateOfficerSelectedPosition(index, positionCd);
    }
  }
  
  // 날짜 자동 설정 (오늘 날짜)
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const todayStr = `${year}-${month}-${day}`;
  updateOfficerDateOfCessation(index, todayStr);
  
  // Reason for Cessation 자동 설정 (B12003)
  updateOfficerReasonForCessation(index, 'B12003');
};

// Officer의 직책 옵션을 공통코드에서 가져온 positionOptions와 매칭하여 반환
const getOfficerPositionOptions = (index: number) => {
  if (index < 0 || index >= officers.value.length) {
    return [];
  }
  const officer = officers.value[index];
  if (!officer.positions || officer.positions.length === 0) {
    return [];
  }
  
  // 해당 Officer의 positionCd 목록
  const officerPositionCds = officer.positions.map(p => p.positionCd).filter(Boolean);
  
  // 공통코드에서 가져온 positionOptions와 매칭
  return officerCessationStore.positionOptions
    .filter(opt => officerPositionCds.includes(opt.value))
    .map(opt => ({
      title: opt.title,
      value: opt.value
    }));
};

// Cancel 버튼 클릭 시 해당 Officer의 폼 모드 비활성화
const handleCancelOfficer = (index: number) => {
  const selectedPositionCd = officerSelectedPosition.value.get(index);
  
  officerFormMode.value.set(index, false);
  officerDateOfCessation.value.delete(index);
  officerReasonForCessation.value.delete(index);
  officerOtherReason.value.delete(index);
  officerSelectedPosition.value.delete(index);
  officerDateMenuOpen.value.set(index, false);
  officerContactFullInfo.value.delete(index);
  
  // WITHDRAW 모드 해제
  if (withdrawnOfficerIndex.value === index) {
    withdrawnOfficerIndex.value = null;
  }
  
  if (selectedOfficerIndex.value === index) {
    selectedOfficerIndex.value = null;
    officerName.value = '';
    position.value = '';
    appointedDateField.dateValue.value = '';
    dateOfCessationField.dateValue.value = '';
    reasonForCessation.value = '';
    otherReason.value = '';
  }
  
  // Position이 선택되어 있었으면 템플릿 초기화
  if (selectedPositionCd) {
    emit('reset-templates');
  }
};

// Officer 선택 시 처리 (폼 데이터를 메인 폼으로 동기화)
const selectOfficer = async (index: number) => {
  if (index < 0 || index >= officers.value.length) {
    return;
  }

  const selectedOfficer = officers.value[index];
  selectedOfficerIndex.value = index;
  officerName.value = selectedOfficer.contactCd;
  
  // 해당 Officer의 폼 데이터를 메인 폼으로 복사
  const dateVal = officerDateOfCessation.value.get(index);
  if (dateVal) {
    dateOfCessationField.dateValue.value = dateVal;
  }
  const reasonVal = officerReasonForCessation.value.get(index);
  if (reasonVal) {
    reasonForCessation.value = reasonVal;
  }
  const otherReasonVal = officerOtherReason.value.get(index);
  if (otherReasonVal) {
    otherReason.value = otherReasonVal;
  }

  // 선택된 Officer의 Position 사용 (선택된 직책이 있으면 그것 사용, 없으면 첫 번째)
  const selectedPositionCd = officerSelectedPosition.value.get(index) as string | undefined;
  if (selectedPositionCd) {
    position.value = selectedPositionCd;
  } else if (selectedOfficer.positions && selectedOfficer.positions.length > 0) {
    const firstPosition = selectedOfficer.positions[0];
    const firstPositionCd = firstPosition.positionCd || '';
    position.value = firstPositionCd;
    if (selectedOfficer.positions.length === 1 && firstPositionCd) {
      // 직책이 하나면 자동 선택 (문서 자동 선택 트리거를 위해 updateOfficerSelectedPosition 호출)
      updateOfficerSelectedPosition(index, firstPositionCd);
    }
    
    // APPOINTMENT_DATE 바인딩 (선택된 직책이 있으면 해당 직책의 날짜 사용)
    const positionToUse = selectedPositionCd 
      ? selectedOfficer.positions.find(p => p.positionCd === selectedPositionCd) || firstPosition
      : firstPosition;
    if (positionToUse.appointmentDate) {
      try {
        const date = new Date(positionToUse.appointmentDate);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          appointedDateField.dateValue.value = `${year}-${month}-${day}`;
          console.log('✅ APPOINTMENT_DATE 바인딩:', appointedDateField.dateValue.value);
        } else {
          appointedDateField.dateValue.value = '';
        }
      } catch (error) {
        console.error('❌ Failed to parse appointment date:', error);
        appointedDateField.dateValue.value = '';
      }
    } else {
      appointedDateField.dateValue.value = '';
    }
  } else {
    position.value = '';
    appointedDateField.dateValue.value = '';
  }

  // Officer 주소 정보 조회
  try {
    const response = await contactsApi.getContactById(selectedOfficer.contactCd);
    const contact = (response as any).contact || response.data?.contact;
    if (contact) {
      officerAddressInfo.value = {
        ADDRESS_TYPE_CD: contact.ADDRESS_TYPE_CD || contact.addressTypeCd || '',
        POSTAL_CODE: contact.POSTAL_CODE || contact.postalCode || '',
        BLOCK_NO: contact.BLOCK_NO || contact.blockNo || '',
        STREET_NAME: contact.STREET_NAME || contact.streetName || '',
        BUILDING_NAME: contact.BUILDING_NAME || contact.buildingName || '',
        ADDR_LEVEL: contact.ADDR_LEVEL || contact.addrLevel || '',
        ADDR_UNIT: contact.ADDR_UNIT || contact.addrUnit || '',
        FOREIGN_ADDRESS_LINE_1: contact.FOREIGN_ADDRESS_LINE_1 || contact.foreignAddressLine1 || '',
        FOREIGN_ADDRESS_LINE_2: contact.FOREIGN_ADDRESS_LINE_2 || contact.foreignAddressLine2 || ''
      };
      console.log('✅ Officer 주소 정보 조회 완료:', officerAddressInfo.value);
    }
  } catch (error: any) {
    console.error('❌ Failed to fetch officer address info:', error);
    officerAddressInfo.value = {};
  }
};

// 외부에서 폼 데이터 설정 (E-Filing 페이지에서 TASK_DATA로 데이터 채우기용)
const setFormData = async (formData: any) => {
  if (!formData) return;

  console.log('📋 [OfficerCessationForm] setFormData:', formData);

  try {
    // Entity 설정
    if (formData.entity) {
      entity.value = formData.entity;
      // Entity 설정 후 Vue 반응성 시스템이 처리될 때까지 대기
      await nextTick();
      // Officer 목록 로드 대기
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    // Officer 정보가 있으면 해당 Officer 찾아서 WITHDRAW 모드 활성화
    if (formData.officerNameCd && officers.value.length > 0) {
      const officerIndex = officers.value.findIndex(o => o.contactCd === formData.officerNameCd);
      if (officerIndex >= 0) {
        // 해당 Officer의 Position 설정
        if (formData.position) {
          updateOfficerSelectedPosition(officerIndex, formData.position);
        }
        // Date of Cessation 설정
        if (formData.dateOfCessation?.value || formData.dateOfCessation) {
          const dateVal = formData.dateOfCessation?.value || formData.dateOfCessation;
          updateOfficerDateOfCessation(officerIndex, dateVal);
        }
        // Reason for Cessation 설정
        if (formData.reasonForCessation) {
          updateOfficerReasonForCessation(officerIndex, formData.reasonForCessation);
        }
        // Other Reason 설정
        if (formData.otherReason) {
          updateOfficerOtherReason(officerIndex, formData.otherReason);
        }
        // WITHDRAW 모드 활성화
        await handleWithdraw(officerIndex);
      }
    }

    // Task Flow 설정
    if (formData.taskFlow) {
      taskFlow.value = { ...taskFlow.value, ...formData.taskFlow };
    }

    console.log('✅ [OfficerCessationForm] Form data set successfully');
  } catch (error) {
    console.error('❌ [OfficerCessationForm] Failed to set form data:', error);
  }
};

// initialData props가 있으면 자동으로 데이터 설정
watch(() => props.initialData, async (newData) => {
  if (!newData) return;
  
  // E-Filing 모드일 때는 Entity만 설정 (Officer 목록 로드는 불필요)
  if (props.eFilingMode && newData.formData?.entity) {
    const entityCd = newData.formData.entity;
    entity.value = entityCd;
    // PreSet 임베디드에서는 전체 Officer 리스트 대신 단일 편집 필드만 사용한다.
    position.value = newData.formData?.position || 'B30027';
    reasonForCessation.value = newData.formData?.reasonForCessation || '';
    otherReason.value = newData.formData?.otherReason || '';
    const ceasedDate = newData.formData?.dateOfCessation?.value || newData.formData?.dateOfCessation || '';
    dateOfCessationField.dateValue.value = ceasedDate;
    // Entity Options가 로드될 때까지 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Position 옵션도 로드 (Position 이름 표시를 위해)
    try {
      await officerCessationStore.fetchPositions(entityCd);
    } catch (error) {
      console.error('Failed to fetch positions for E-Filing mode:', error);
    }
    
    // Position 코드가 있으면 이름 로드
    if (newData.formData?.position) {
      await loadPositionName(newData.formData.position);
    }
  } else if (newData.formData) {
    await setFormData(newData.formData);
  }
}, { immediate: true });

// 초기 데이터 로드
onMounted(async () => {
  try {
    // Position은 Entity 선택 후에 로드되므로 여기서는 로드하지 않음
    await Promise.all([
      officerCessationStore.fetchEntities(),
      fetchCessationReasons(), // B12 공통코드 조회
      loadIdTypeCodeNames() // B03 ID_TYPE_CD 코드 NAME 로드
    ]);
  } catch (error) {
    console.error('Failed to fetch initial data:', error);
  }
});

const handleNewContact = () => {
  console.log('Open new contact dialog');
};

// 문서 바인딩 변수 매핑 정보
const docVariableMapping = {
  entityName: 'CompanyName',
  entityRegNo: 'RegistrationNo',
  officerName: ['WithdrawOfficerName', 'ResignOfficerName', 'Signer'], // officerName 필드에 여러 변수 매핑
  officerIdNo: 'WithdrawOfficerIdNo', // Officer ID/Registration Number (오타 수정: Withraw -> Withdraw)
  dateOfCessation: 'WithdrawDate',
  officerAddress: ['ResignOfficerAddress', 'WithdrawOfficerAddress'], // ResignOfficerAddress와 WithdrawOfficerAddress 둘 다 매핑
  companyAddress: 'CompanyAddress',
  positionHeld: 'PositionHeld',
  clientNm: 'CSPName'
};

// Officer 주소 포맷팅 함수 (공통 함수 사용)
const formatOfficerAddress = (): string => {
  const address = formatAddress(officerAddressInfo.value, { useComma: true });
  return typeof address === 'string' ? address : '';
};

// Entity 주소 포맷팅 함수 (공통 함수 사용)
const formatEntityAddress = (): string => {
  const address = formatAddress(entityAddressInfo.value, { useComma: true });
  return typeof address === 'string' ? address : '';
};

// 폼 데이터를 doc_variable 정보와 함께 반환하는 메서드
// WITHDRAW로 선택된 Officer의 정보만 사용
const getFormDataWithDocVariables = async () => {
  // WITHDRAW로 선택된 Officer가 없으면 빈 데이터 반환
  if (withdrawnOfficerIndex.value === null) {
    return {};
  }
  
  const index = withdrawnOfficerIndex.value;
  const officer = officers.value[index];
  const contact = officerContactFullInfo.value.get(index);
  
  // WITHDRAW로 선택된 Officer의 데이터 사용
  const entityNameVal = entityName.value || '';
  const entityRegNoVal = entityRegNo.value || '';
  const officerNameVal = officer.contactName || officerDisplayName.value || ''; // 실제 이름 사용
  
  // Officer ID/Registration Number: officerNameCd (contactCd)로 Contact 조회하여 CONTACT_REG_NO 가져오기
  let officerIdNoVal = '';
  const officerNameCd = officer.contactCd || '';
  
  if (officerNameCd) {
    // 먼저 officerContactFullInfo에서 확인
    if (contact) {
      officerIdNoVal = contact.CONTACT_REG_NO || contact.contactRegNo || '';
    }
    
    // officerContactFullInfo에 없으면 Contact API 호출
    if (!officerIdNoVal) {
      try {
        const contactResponse = await contactsApi.getContactById(officerNameCd);
        const contactData = (contactResponse as any).contact || contactResponse.data?.contact;
        if (contactData) {
          officerIdNoVal = contactData.CONTACT_REG_NO || contactData.contactRegNo || '';
        }
      } catch (error) {
        console.error('❌ Failed to fetch contact for officerIdNo:', error);
      }
    }
  }
  
  const dateOfCessationVal = officerDateOfCessation.value.get(index) || dateOfCessationField.dateValue.value || '';
  const officerAddressVal = formatOfficerAddress();
  const entityAddressVal = formatEntityAddress();

  // 필드명(키)을 기준으로 그룹화하여 같은 필드에 여러 doc_variable이 있으면 쉼표로 구분
  const fieldMap = new Map<string, { value: any; docVariables: string[] }>();

  // entityName
  if (entityNameVal) {
    fieldMap.set('entityName', {
      value: entityNameVal,
      docVariables: [docVariableMapping.entityName]
    });
  }

  // entityRegNo
  if (entityRegNoVal) {
    fieldMap.set('entityRegNo', {
      value: entityRegNoVal,
      docVariables: [docVariableMapping.entityRegNo]
    });
  }

  // officerName 필드에 여러 doc_variable 매핑 (#WithdrawOfficerName, #ResignOfficerName, #Signer)
  if (officerNameVal) {
    const officerNameVars = Array.isArray(docVariableMapping.officerName) 
      ? docVariableMapping.officerName 
      : [docVariableMapping.officerName];
    fieldMap.set('officerName', {
      value: officerNameVal,
      docVariables: officerNameVars
    });
  }

  // officerIdNo 필드에 doc_variable 매핑 (#WithdrawOfficerIdNo)
  // 값이 없어도 doc_variable은 추가 (eFilingData의 registrationIdNo와 동일한 값 사용)
  fieldMap.set('officerIdNo', {
    value: officerIdNoVal || '',
    docVariables: [docVariableMapping.officerIdNo]
  });

  // dateOfCessation 필드에 doc_variable 매핑 (#WithdrawDate)
  // doc_variables로 매핑되는 날짜는 formatDocDate로 변환하여 저장
  if (dateOfCessationVal) {
    const formattedDateOfCessation = formatDocDate(dateOfCessationVal);
    fieldMap.set('dateOfCessation', {
      value: formattedDateOfCessation,
      docVariables: [docVariableMapping.dateOfCessation]
    });
  }

  // officerAddress - ResignOfficerAddress와 WithdrawOfficerAddress 둘 다 매핑
  if (officerAddressVal) {
    const officerAddressVars = Array.isArray(docVariableMapping.officerAddress) 
      ? docVariableMapping.officerAddress 
      : [docVariableMapping.officerAddress];
    fieldMap.set('officerAddress', {
      value: officerAddressVal,
      docVariables: officerAddressVars
    });
  }

  // companyAddress
  if (entityAddressVal) {
    fieldMap.set('companyAddress', {
      value: entityAddressVal,
      docVariables: [docVariableMapping.companyAddress]
    });
  }

  // positionHeld (선택된 포지션 이름)
  const selectedPositionCd = officerSelectedPosition.value.get(index) as string | undefined;
  if (selectedPositionCd) {
    const selectedPosition = positionOptions.value.find(opt => opt.value === selectedPositionCd);
    const positionName = selectedPosition?.title || '';
    if (positionName) {
      fieldMap.set('positionHeld', {
        value: positionName,
        docVariables: [docVariableMapping.positionHeld]
      });
    }
  }

  // clientNm (값은 CreateTaskForm.vue에서 채움, 여기서는 doc_variables만 설정)
  // clientNm은 외부에서 주입되므로 여기서는 빈 값으로 doc_variables만 설정
  fieldMap.set('clientNm', {
    value: '', // CreateTaskForm.vue에서 실제 값으로 채워짐
    docVariables: [docVariableMapping.clientNm]
  });

  // Entity 관련 공통 데이터 조회 및 doc_variables 생성
  if (entity.value) {
    try {
      const entityData = await getEntityData(entity.value);
      
      // {#CompanyName} - entityName에 추가 (기존 필드와 병합)
      if (entityData.companyName) {
        const existingField = fieldMap.get('entityName');
        if (existingField) {
          if (!existingField.docVariables.includes('CompanyName')) {
            existingField.docVariables.push('CompanyName');
          }
        } else {
          fieldMap.set('entityName', {
            value: entityData.companyName,
            docVariables: ['CompanyName']
          });
        }
      }

      // {#RegistrationNo} - entityRegNo에 추가 (기존 필드와 병합)
      if (entityData.registrationNumber) {
        const existingField = fieldMap.get('entityRegNo');
        if (existingField) {
          if (!existingField.docVariables.includes('RegistrationNo')) {
            existingField.docVariables.push('RegistrationNo');
          }
        } else {
          fieldMap.set('entityRegNo', {
            value: entityData.registrationNumber,
            docVariables: ['RegistrationNo']
          });
        }
      }

      // {#IncorporatedDate} - DATE_OF_INCORPORATION 날짜 형식 (formatDocDate 사용)
      if (entityData.incorporationDate) {
        // DD/MM/YYYY 형식을 YYYY-MM-DD로 변환
        let dateToFormat = entityData.incorporationDate;
        const ddMMyyyyMatch = String(dateToFormat).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddMMyyyyMatch) {
          const [, day, month, year] = ddMMyyyyMatch;
          dateToFormat = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        
        const formattedDate = formatDocDate(dateToFormat);
        if (formattedDate) {
          fieldMap.set('incorporatedDate', {
            value: formattedDate,
            docVariables: ['IncorporatedDate']
          });
        }
      }

      // {#CompanyAddress}
      if (entityData.companyAddress) {
        fieldMap.set('companyAddress', {
          value: entityData.companyAddress,
          docVariables: ['CompanyAddress']
        });
      }

      // {#FinancialYearEnd} - FINANCIAL_YEAR_END 날짜 형식 (formatDocDate 사용)
      if (entityData.financialYearEnd) {
        // DD/MM/YYYY 형식을 YYYY-MM-DD로 변환
        let dateToFormat = entityData.financialYearEnd;
        const ddMMyyyyMatch = String(dateToFormat).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddMMyyyyMatch) {
          const [, day, month, year] = ddMMyyyyMatch;
          dateToFormat = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        
        const formattedDate = formatDocDate(dateToFormat);
        if (formattedDate) {
          fieldMap.set('financialYearEnd', {
            value: formattedDate,
            docVariables: ['FinancialYearEnd']
          });
        }
      }

      // {#PrimaryActivityCode}
      if (entityData.primaryActivityCode) {
        fieldMap.set('primaryActivityCode', {
          value: entityData.primaryActivityCode,
          docVariables: ['PrimaryActivityCode']
        });
      }

      // {#PrimaryActivityDescription}
      if (entityData.primaryActivityDescription) {
        fieldMap.set('primaryActivityDescription', {
          value: entityData.primaryActivityDescription,
          docVariables: ['PrimaryActivityDescription']
        });
      }

      // {#SecondaryActivityCode}
      if (entityData.secondaryActivityCode) {
        fieldMap.set('secondaryActivityCode', {
          value: entityData.secondaryActivityCode,
          docVariables: ['SecondaryActivityCode']
        });
      }

      // {#SecondaryActivityDescription}
      if (entityData.secondaryActivityDescription) {
        fieldMap.set('secondaryActivityDescription', {
          value: entityData.secondaryActivityDescription,
          docVariables: ['SecondaryActivityDescription']
        });
      }

      // {#EntityJurisdiction}
      if (entityData.entityJurisdiction) {
        fieldMap.set('entityJurisdiction', {
          value: entityData.entityJurisdiction,
          docVariables: ['EntityJurisdiction']
        });
      }

      // {#EntityType}
      if (entityData.entityType) {
        fieldMap.set('entityType', {
          value: entityData.entityType,
          docVariables: ['EntityType']
        });
      }

      // {#CompanyEmail}
      if (entityData.companyEmail) {
        fieldMap.set('companyEmail', {
          value: entityData.companyEmail,
          docVariables: ['CompanyEmail']
        });
      }
    } catch (error) {
      console.error('❌ [OfficerCessationForm] Entity 데이터 조회 실패:', error);
    }
  }

  // 결과 객체 생성 - 각 필드를 { fieldName: { value: "...", doc_variables: ["#..."] } } 형태로 변환
  // 형태: { entityName: { value: "MONTERE PTE. LTD.", doc_variables: ["#CompanyName"] } }
  const result: Record<string, any> = {};
  
  fieldMap.forEach((item, fieldName) => {
    // doc_variables를 배열로 변환
    const docVariablesArray = item.docVariables.map(v => `#${v}`);
    
    // 각 필드를 { value: "...", doc_variables: ["#..."] } 형태의 객체로 저장
    result[fieldName] = {
      value: item.value,
      doc_variables: docVariablesArray
    };
  });
  
  // WITHDRAW로 선택된 Officer의 추가 필드들 포함 (doc_variable 없는 일반 필드)
  if (withdrawnOfficerIndex.value !== null) {
    const index = withdrawnOfficerIndex.value;
    const selectedPositionCd = officerSelectedPosition.value.get(index) as string | undefined;
    const reasonVal = officerReasonForCessation.value.get(index) as string | undefined;
    const otherReasonVal = officerOtherReason.value.get(index) as string | undefined;
    
    // position, reasonForCessation, otherReason은 일반 필드로 추가
    if (selectedPositionCd) {
      (result as any).position = selectedPositionCd;
    }
    if (reasonVal) {
      (result as any).reasonForCessation = reasonVal;
    }
    if (otherReasonVal) {
      (result as any).otherReason = otherReasonVal;
    }
    // officerNameCd (CONTACT_CD) 추가
    const officer = officers.value[index];
    if (officer?.contactCd) {
      (result as any).officerNameCd = officer.contactCd;
    }
  }
  
  return result;
};

// WITHDRAW로 선택된 Officer의 withdrawOfficerData 생성 (deprecated - getEFilingData 사용)
const getWithdrawOfficerData = () => {
  // WITHDRAW로 선택된 Officer가 없으면 null 반환
  if (withdrawnOfficerIndex.value === null) {
    return null;
  }
  
  const index = withdrawnOfficerIndex.value;
  const officer = officers.value[index];
  const contact = officerContactFullInfo.value.get(index);
  
  // Contact 정보가 없으면 null 반환
  if (!contact) {
    console.warn('⚠️ Contact 정보가 없습니다. withdrawOfficerData를 생성할 수 없습니다.');
    return null;
  }
  
  // 사용자가 제공한 컬럼 목록에 따라 Contact 정보에서 데이터 추출
  const withdrawOfficerData: any = {
    CONTACT_CD: contact.CONTACT_CD || contact.contactCd || '',
    CONTACT_REGISTER_TYPE: contact.CONTACT_REGISTER_TYPE || contact.contactRegisterType || '',
    PROFILE_ID: contact.PROFILE_ID || contact.profileId || '',
    CONTACT_REG_NO: contact.CONTACT_REG_NO || contact.contactRegNo || '',
    CONTACT_NAME: contact.CONTACT_NAME || contact.contactName || '',
    CATEGORY_CD: contact.CATEGORY_CD || contact.categoryCd || '',
    FIRST_NAME: contact.FIRST_NAME || contact.firstName || '',
    MIDDLE_NAME: contact.MIDDLE_NAME || contact.middleName || '',
    SURNAME: contact.SURNAME || contact.surname || '',
    NAME_LAYOUT_CD: contact.NAME_LAYOUT_CD || contact.nameLayoutCd || '',
    DATE_OF_BIRTH2: contact.DATE_OF_BIRTH2 || contact.dateOfBirth2 || '',
    ID_TYPE_CD: contact.ID_TYPE_CD || contact.idTypeCd || '',
    NATIONALITY_SCRAPING: contact.NATIONALITY_SCRAPING || contact.nationalityScraping || '',
    NATIONALITY_CD: contact.NATIONALITY_CD || contact.nationalityCd || '',
    ADDRESS_TYPE_CD: contact.ADDRESS_TYPE_CD || contact.addressTypeCd || '',
    POSTAL_CODE: contact.POSTAL_CODE || contact.postalCode || '',
    BLOCK_NO: contact.BLOCK_NO || contact.blockNo || '',
    STREET_NAME: contact.STREET_NAME || contact.streetName || '',
    BUILDING_NAME: contact.BUILDING_NAME || contact.buildingName || '',
    ADDR_LEVEL: contact.ADDR_LEVEL || contact.addrLevel || '',
    ADDR_UNIT: contact.ADDR_UNIT || contact.addrUnit || '',
    FOREIGN_ADDRESS_LINE_1: contact.FOREIGN_ADDRESS_LINE_1 || contact.foreignAddressLine1 || '',
    FOREIGN_ADDRESS_LINE_2: contact.FOREIGN_ADDRESS_LINE_2 || contact.foreignAddressLine2 || '',
    ALTERNATE_ADDRESS_CD: contact.ALTERNATE_ADDRESS_CD || contact.alternateAddressCd || '',
    ALT_POSTAL_CODE: contact.ALT_POSTAL_CODE || contact.altPostalCode || '',
    ALT_BLOCK_NO: contact.ALT_BLOCK_NO || contact.altBlockNo || '',
    ALT_STREET_NAME: contact.ALT_STREET_NAME || contact.altStreetName || '',
    ALT_BUILDING_NAME: contact.ALT_BUILDING_NAME || contact.altBuildingName || '',
    ALT_ADDR_LEVEL: contact.ALT_ADDR_LEVEL || contact.altAddrLevel || '',
    ALT_ADDR_UNIT: contact.ALT_ADDR_UNIT || contact.altAddrUnit || '',
    ALT_FOREIGN_ADDRESS_LINE_1: contact.ALT_FOREIGN_ADDRESS_LINE_1 || contact.altForeignAddressLine1 || '',
    ALT_FOREIGN_ADDRESS_LINE_2: contact.ALT_FOREIGN_ADDRESS_LINE_2 || contact.altForeignAddressLine2 || '',
    CONTACT_ADDRESS_EFFECTIVE_DATE: contact.CONTACT_ADDRESS_EFFECTIVE_DATE || contact.contactAddressEffectiveDate || '',
    CONTACT_EMAIL: contact.CONTACT_EMAIL || contact.contactEmail || '',
    COUNTRY_SCRAPING: contact.COUNTRY_SCRAPING || contact.countryScraping || '',
    COUNTRY_CD: contact.COUNTRY_CD || contact.countryCd || '',
    AREA_CODE: contact.AREA_CODE || contact.areaCode || '',
    MOBILE_NO: contact.MOBILE_NO || contact.mobileNo || '',
    AUDITOR_YN: contact.AUDITOR_YN || contact.auditorYn || '',
    DATE_OF_INCORPORATION: contact.DATE_OF_INCORPORATION || contact.dateOfIncorporation || '',
    JURISDICTION_SCRAPING: contact.JURISDICTION_SCRAPING || contact.jurisdictionScraping || '',
    JURISDICTION: contact.JURISDICTION || contact.jurisdiction || '',
    REPRESENTATIVE_CD: contact.REPRESENTATIVE_CD || contact.representativeCd || '',
    NOTE: contact.NOTE || contact.note || '',
    IS_DEL: contact.IS_DEL || contact.isDel || '',
    DEL_DATE: contact.DEL_DATE || contact.delDate || '',
    SYNC_DATE: contact.SYNC_DATE || contact.syncDate || '',
    VERIFY_CODE: contact.VERIFY_CODE || contact.verifyCode || '',
    USER_AGENT: contact.USER_AGENT || contact.userAgent || '',
    FINGER_PRINT: contact.FINGER_PRINT || contact.fingerPrint || '',
    LOGIN_REGION: contact.LOGIN_REGION || contact.loginRegion || '',
    MYLETTAGA_LOGIN_DATE: contact.MYLETTAGA_LOGIN_DATE || contact.myLettagaLoginDate || '',
    BIGO: contact.BIGO || contact.bigo || '',
    SYSREGDATE: contact.SYSREGDATE || contact.sysRegDate || '',
    SYSREGID: contact.SYSREGID || contact.sysRegId || '',
    SYSREGNAME: contact.SYSREGNAME || contact.sysRegName || '',
    SYSMODDATE: contact.SYSMODDATE || contact.sysModDate || '',
    SYSMODID: contact.SYSMODID || contact.sysModId || '',
    SYSMODNAME: contact.SYSMODNAME || contact.sysModName || '',
    SYSTIMEZONE: contact.SYSTIMEZONE || contact.sysTimeZone || '',
    GLOBAL_USER_CD: contact.GLOBAL_USER_CD || contact.globalUserCd || '',
    DATE_OF_BIRTH3: contact.DATE_OF_BIRTH3 || contact.dateOfBirth3 || '',
    DATE_OF_BIRTH: contact.DATE_OF_BIRTH || contact.dateOfBirth || '',
    LEGAL_FORM: contact.LEGAL_FORM || contact.legalForm || '',
    INCORPORATION_STATUTE: contact.INCORPORATION_STATUTE || contact.incorporationStatute || '',
    CORPORATE_ENTITY_REGISTER: contact.CORPORATE_ENTITY_REGISTER || contact.corporateEntityRegister || ''
  };
  
  return withdrawOfficerData;
};

// ========================================
// 필수값 검증 관련 코드
// ========================================

// Entity 검증 함수
const validateEntity = (): boolean => {
  if (!entity.value) {
    internalValidationErrors.value.entity = t('startTask.This field is required') || 'This field is required';
    // Entity 필드에 포커스 주기 위해 nextTick 사용
    nextTick(() => {
      if (entityInputRef.value) {
        try {
          // Vuetify 컴포넌트의 focus 메서드 호출
          if (typeof entityInputRef.value.focus === 'function') {
            entityInputRef.value.focus();
          }
          // 스크롤 이동
          const element = entityInputRef.value.$el || entityInputRef.value.$refs?.input?.$el || document.querySelector('[data-field="entityName"]')?.closest('.v-autocomplete');
          if (element) {
            (element as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } catch (error) {
          console.warn('Failed to focus entity input:', error);
        }
      } else {
        // ref가 없을 경우 fallback으로 DOM 쿼리 사용
        const entityInput = document.querySelector('[data-field="entityName"]')?.closest('.v-autocomplete');
        if (entityInput) {
          (entityInput as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = (entityInput as HTMLElement).querySelector('input');
          if (input) {
            input.focus();
          }
        }
      }
    });
    return false;
  }
  // 검증 통과 시 에러 제거
  if (internalValidationErrors.value.entity) {
    delete internalValidationErrors.value.entity;
  }
  return true;
};

// Date of Cessation 검증 함수 (withdrawnOfficerIndex 기준)
const validateDateOfCessation = (): boolean => {
  if (withdrawnOfficerIndex.value === null) {
    internalValidationErrors.value.dateOfCessation = t('startTask.Please select an officer to withdraw') || 'Please select an officer to withdraw';
    return false;
  }
  
  const index = withdrawnOfficerIndex.value;
  const dateVal = officerDateOfCessation.value.get(index);
  
  if (!dateVal) {
    internalValidationErrors.value.dateOfCessation = t('startTask.This field is required') || 'This field is required';
    nextTick(() => {
      // 해당 Officer의 Date of Cessation 필드 찾기
      const dateInput = document.querySelector(`[data-officer-index="${index}"] [data-field="dateOfCessation"]`)?.closest('.v-text-field');
      if (dateInput) {
        (dateInput as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = (dateInput as HTMLElement).querySelector('input');
        if (input) {
          input.focus();
        }
      }
    });
    return false;
  }
  // 검증 통과 시 에러 제거
  if (internalValidationErrors.value.dateOfCessation) {
    delete internalValidationErrors.value.dateOfCessation;
  }
  return true;
};

// Reason for Cessation 검증 함수 (withdrawnOfficerIndex 기준)
const validateReasonForCessation = (): boolean => {
  if (withdrawnOfficerIndex.value === null) {
    internalValidationErrors.value.reasonForCessation = t('startTask.Please select an officer to withdraw') || 'Please select an officer to withdraw';
    return false;
  }
  
  const index = withdrawnOfficerIndex.value;
  const reasonVal = officerReasonForCessation.value.get(index);
  
  if (!reasonVal) {
    internalValidationErrors.value.reasonForCessation = t('startTask.This field is required') || 'This field is required';
    nextTick(() => {
      // 해당 Officer의 Reason for Cessation 필드 찾기
      const reasonInput = document.querySelector(`[data-officer-index="${index}"] [data-field="reasonForCessation"]`)?.closest('.v-select');
      if (reasonInput) {
        (reasonInput as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = (reasonInput as HTMLElement).querySelector('input');
        if (input) {
          input.focus();
        }
      }
    });
    return false;
  }
  // 검증 통과 시 에러 제거
  if (internalValidationErrors.value.reasonForCessation) {
    delete internalValidationErrors.value.reasonForCessation;
  }
  return true;
};

// Position 검증 함수 (withdrawnOfficerIndex 기준)
const validatePosition = (): boolean => {
  if (withdrawnOfficerIndex.value === null) {
    internalValidationErrors.value.position = t('startTask.Please select an officer to withdraw') || 'Please select an officer to withdraw';
    return false;
  }
  
  const index = withdrawnOfficerIndex.value;
  const positionVal = officerSelectedPosition.value.get(index);
  
  if (!positionVal) {
    internalValidationErrors.value.position = t('startTask.This field is required') || 'This field is required';
    nextTick(() => {
      // 해당 Officer의 Position 필드 찾기
      const positionInput = document.querySelector(`[data-officer-index="${index}"] [data-field="position"]`)?.closest('.v-select');
      if (positionInput) {
        (positionInput as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = (positionInput as HTMLElement).querySelector('input');
        if (input) {
          input.focus();
        }
      }
    });
    return false;
  }
  // 검증 통과 시 에러 제거
  if (internalValidationErrors.value.position) {
    delete internalValidationErrors.value.position;
  }
  return true;
};

// 전체 필수 필드 검증 함수 (모든 필수 필드를 순차적으로 검증)
const validateAll = (): boolean => {
  // 모든 validation 에러 초기화
  internalValidationErrors.value = {};
  
  // WITHDRAW로 선택된 Officer가 없으면 에러
  if (withdrawnOfficerIndex.value === null) {
    internalValidationErrors.value.entity = t('startTask.Please select an officer to withdraw') || 'Please select an officer to withdraw';
    return false;
  }
  
  // 순차적으로 검증 (첫 번째 실패 시 즉시 반환)
  const isEntityValid = validateEntity();
  if (!isEntityValid) {
    return false;
  }
  
  const isPositionValid = validatePosition();
  if (!isPositionValid) {
    return false;
  }
  
  const isDateOfCessationValid = validateDateOfCessation();
  if (!isDateOfCessationValid) {
    return false;
  }
  
  const isReasonForCessationValid = validateReasonForCessation();
  if (!isReasonForCessationValid) {
    return false;
  }
  
  // 모든 검증 통과
  return true;
};

// Validation error messages (computed)
const dateOfCessationErrorMessages = computed(() => {
  if (internalValidationErrors.value.dateOfCessation) {
    return [internalValidationErrors.value.dateOfCessation];
  }
  return [];
});

const reasonForCessationErrorMessages = computed(() => {
  if (internalValidationErrors.value.reasonForCessation) {
    return [internalValidationErrors.value.reasonForCessation];
  }
  return [];
});

const positionErrorMessages = computed(() => {
  if (internalValidationErrors.value.position) {
    return [internalValidationErrors.value.position];
  }
  return [];
});

const entityErrorMessages = computed(() => {
  if (internalValidationErrors.value.entity) {
    return [internalValidationErrors.value.entity];
  }
  return [];
});

// ========================================
// E-Filing JSON 생성 관련 코드
// ========================================

// E-Filing용 JSON 데이터 생성 함수 (async)
// 백엔드의 createWithdrawOfficerJson과 동일한 구조를 만들되,
// WithdrawList 배열로 감싸고 EntityUEN, FilingAgentUEN 추가
// 실제 데이터 변환은 백엔드에서 처리되므로 여기서는 기본 구조만 생성
const getEFilingData = async () => {
  const mapPositionToBizFile = (pos: string): string => {
    const p = String(pos || '').trim();
    if (!p) return '';
    // 이미 BizFile 코드면 그대로 사용
    if (/^[A-Z]{2}_.+/.test(p)) return p;
    // B30 -> BizFile 코드 매핑 (현재 PreSet 요구 범위 우선)
    const map: Record<string, string> = {
      B30027: 'LC_DIR', // Director
      B30025: 'LC_SEC', // Secretary (alternate code)
      B30044: 'LC_DIR', // Nominee Director
      /** Lettaga SG: B30033 = Company Secretary (appoint/withdraw secretary, L_OFFICER) — BizFile position is LC_SEC */
      B30033: 'LC_SEC',
    };
    return map[p] || '';
  };

  const mapReasonToBizFile = (reason: string): string => {
    const r = String(reason || '').trim();
    if (!r) return '';
    // 이미 BizFile 코드면 그대로 사용
    if (/^[A-Z_]+$/.test(r) && !/^B\d+/.test(r)) return r;
    const map: Record<string, string> = {
      B12003: 'RESIGN',
      B12004: 'OTH',
    };
    return map[r] || '';
  };

  const normalizeDateYmd = (value: string): string => {
    const s = String(value || '').trim();
    if (!s) return '';
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const slash = s.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
    if (slash) return `${slash[1]}-${slash[2]}-${slash[3]}`;
    return s;
  };

  // E-Filing 모드에서는 initialData에서 정보 가져오기
  if (props.eFilingMode) {
    const customerInfo = sessionStore.getCustomerInfo();
    const fd = props.initialData?.formData || {};
    const existingEFilingData = props.initialData?.eFilingData || {};
    const existingWithdrawList = existingEFilingData.WithdrawList || existingEFilingData.withdrawList || [];
    const existingWithdrawItem = Array.isArray(existingWithdrawList) ? existingWithdrawList[0] : null;
    const existingHolder = existingWithdrawItem?.withdrawPositionHolder || {};
    const existingHeld = existingHolder?.withdrawPositionHeld?.[0] || {};

    const selectedPositionCd = String(position.value || fd.position || 'B30027').trim();
    const ceasedDate = normalizeDateYmd(
      String(dateOfCessationField.dateValue.value || fd.dateOfCessation?.value || fd.dateOfCessation || ''),
    );
    const reasonCd = String(reasonForCessation.value || fd.reasonForCessation || '').trim();
    const ceasedReasonOther = reasonCd === 'B12004'
      ? String(otherReason.value || fd.otherReason || '').trim()
      : '';

    const extractContactFields = (raw: any) => {
      if (!raw || typeof raw !== 'object') return { profileId: '', registrationIdNo: '' };
      return {
        profileId: String(raw.PROFILE_ID || raw.profileId || '').trim(),
        registrationIdNo: String(raw.CONTACT_REG_NO || raw.contactRegNo || '').trim(),
      };
    };

    let resolvedProfileId = String(existingHolder?.profileId || fd.profileId || '').trim();
    let resolvedRegistrationIdNo = String(existingHolder?.registrationIdNo || fd.registrationIdNo || '').trim();

    // 1) officerNameCd가 있으면 해당 contact 우선 조회
    try {
      const officerNameCd = String(fd.officerNameCd || '').trim();
      if (officerNameCd && (!resolvedProfileId || !resolvedRegistrationIdNo)) {
        const contactRes = await contactsApi.getContactById(officerNameCd);
        const contact = (contactRes as any)?.contact || (contactRes as any)?.data?.contact || null;
        const parsed = extractContactFields(contact);
        resolvedProfileId = resolvedProfileId || parsed.profileId;
        resolvedRegistrationIdNo = resolvedRegistrationIdNo || parsed.registrationIdNo;
      }
    } catch (e) {
      console.warn('[OfficerCessationForm] officerNameCd contact lookup failed:', e);
    }

    // 2) 없으면 entity + officer name으로 대상 officer 탐색 후 L_GLOBAL_CONTACT 조회
    try {
      const entityCdForLookup = String(fd.entity || entity.value || '').trim();
      const officerNameForLookup = String(
        fd.officerName || fd.officerDisplayName || fd.resign_director_name || '',
      ).trim();
      if (
        entityCdForLookup &&
        officerNameForLookup &&
        (!resolvedProfileId || !resolvedRegistrationIdNo)
      ) {
        const { apiClient } = await import('@/shared/api/client');
        const officersRes: any = await apiClient.get(`/startTask/common/officers-by-entity-all/${entityCdForLookup}`);
        const officerList = officersRes?.data?.officers || [];
        const found = officerList.find((o: any) =>
          String(o?.contactName || '').trim().toLowerCase() === officerNameForLookup.toLowerCase(),
        );
        const foundContactCd = String(found?.contactCd || '').trim();
        if (foundContactCd) {
          const contactRes = await contactsApi.getContactById(foundContactCd);
          const contact = (contactRes as any)?.contact || (contactRes as any)?.data?.contact || null;
          const parsed = extractContactFields(contact);
          resolvedProfileId = resolvedProfileId || parsed.profileId;
          resolvedRegistrationIdNo = resolvedRegistrationIdNo || parsed.registrationIdNo;
        }
      }
    } catch (e) {
      console.warn('[OfficerCessationForm] officer-by-name contact lookup failed:', e);
    }

    const entityUEN =
      entityRegNo.value ||
      fd.entityRegNo ||
      fd.company_registration_no ||
      props.initialData?.entityUEN ||
      existingEFilingData.EntityUEN ||
      '';
    const filingAgentUEN = customerInfo?.regNo || existingEFilingData.FilingAgentUEN || entityUEN || '';

    const eFilingData = {
      WithdrawList: [
        {
          entityType: existingWithdrawItem?.entityType || 'LC',
          withdrawPositionHolder: {
            profileId: resolvedProfileId,
            officerCategory: existingHolder?.officerCategory || fd.officerCategory || '1',
            withdrawPositionHeld: [
              {
                positionHeld:
                  mapPositionToBizFile(selectedPositionCd) ||
                  existingHeld?.positionHeld ||
                  'LC_DIR',
                withdrawAlternateDirectors: null,
                ceasedDate: ceasedDate || existingHeld?.ceasedDate || '',
                ceasedReason:
                  mapReasonToBizFile(reasonCd) ||
                  existingHeld?.ceasedReason ||
                  '',
                deathCertificateDoc: null,
                disqualifiedReason: null,
                disqualifiedReasonSubsection: null,
                ceasedReasonOther,
              },
            ],
            registerAction: 'WITHDRAW',
            registrationIdNo: resolvedRegistrationIdNo,
          },
        },
      ],
      EntityUEN: entityUEN,
      FilingAgentUEN: filingAgentUEN,
    } as any;

    // 하위 호환: 기존 값에 있던 AddList/추가 필드를 유지
    if (existingEFilingData && typeof existingEFilingData === 'object') {
      for (const [k, v] of Object.entries(existingEFilingData)) {
        if (!(k in eFilingData)) {
          (eFilingData as any)[k] = v;
        }
      }
    }

    console.log('📋 [OfficerCessationForm] getEFilingData (E-Filing 모드):', eFilingData);
    return eFilingData;
  }
  
  // 일반 모드: WITHDRAW로 선택된 Officer가 없으면 null 반환
  if (withdrawnOfficerIndex.value === null) {
    return null;
  }
  
  // 세션에서 Client 정보 가져오기
  const customerInfo = sessionStore.getCustomerInfo();
  const filingAgentUEN = customerInfo?.regNo || '';
  
  // EntityUEN은 entityRegNo 사용
  const entityUEN = entityRegNo.value || '';
  
  // 백엔드에서 createWithdrawOfficerJson을 통해 withdrawOfficerData를 생성하므로
  // 프론트엔드에서는 기본 구조만 만들고, 실제 데이터는 백엔드에서 채움
  // 따라서 여기서는 빈 WithdrawList 배열과 EntityUEN, FilingAgentUEN만 반환
  // 백엔드에서 withdrawOfficerData를 받아서 WithdrawList로 변환
  
  const eFilingData = {
    WithdrawList: [], // 백엔드에서 채워짐
    EntityUEN: entityUEN,
    FilingAgentUEN: filingAgentUEN
  };
  
  console.log('📋 [OfficerCessationForm] getEFilingData:', eFilingData);
  return eFilingData;
};

// 외부에서 접근 가능하도록 expose (CreateTaskForm에서 entity 값 가져오기 위해)
defineExpose({
  entity,
  entityName, // Entity 실제 이름
  entityRegNo, // Entity REG_NO
  officerName, // CONTACT_CD (GUID)
  officerDisplayName, // Officer 실제 이름
  position,
  appointedDate: appointedDateField.dateValue, // YYYY-MM-DD 형식의 실제 날짜 값 (OfficerAppointmentForm과 동일)
  dateOfCessation: dateOfCessationField.dateValue,
  reasonForCessation,
  otherReason, // B12004 선택 시 입력한 텍스트
  officerAddressInfo, // Officer 주소 정보
  entityAddressInfo, // Entity 주소 정보
  taskFlow, // Task Flow 상태
  getFormDataWithDocVariables, // doc_variable 정보를 포함한 폼 데이터 반환
  getWithdrawOfficerData, // WITHDRAW로 선택된 Officer의 withdrawOfficerData 반환 (deprecated)
  getEFilingData, // E-Filing용 JSON 데이터 반환 함수
  getIdTypeCodeName, // B03 ID_TYPE_CD 코드 NAME 조회 헬퍼 함수
  setFormData, // E-Filing 페이지에서 TASK_DATA 설정용
  validateEntity, // Entity 검증 함수
  validateDateOfCessation, // Date of Cessation 검증 함수
  validateReasonForCessation, // Reason for Cessation 검증 함수
  validatePosition, // Position 검증 함수
  validateAll // 전체 필수 필드 검증 함수
});
</script>

<template>
  <v-card
    variant="outlined"
    class="mb-6"
    style="border: 1px solid #e0e0e0"
  >
    <v-card-title class="text-h6 pa-4 pb-2">{{ t('startTask.Officer Cessation Details') || 'Officer Cessation Details' }}</v-card-title>
    <v-card-text class="pa-6 pt-2">
      <!-- E-Filing 모드: PreSet 임베디드에서는 단일 편집 폼만 표시 (전체 Officer 리스트 미노출) -->
      <div v-if="eFilingMode" class="mb-6">
        <!-- Entity Name (회사 이름) -->
        <div class="mb-4" style="max-width: 70%;">
          <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
            {{ t('startTask.Entity') || 'Entity' }}
          </label>
          <v-text-field
            :model-value="entityName"
            variant="outlined"
            density="compact"
            readonly
            hide-details
            class="readonly-field"
          ></v-text-field>
        </div>

        <!-- Officer Name (이름) -->
        <div class="mb-4" style="max-width: 70%;">
          <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
            {{ t('startTask.Officer Name') || 'Officer Name' }}
          </label>
          <v-text-field
            :model-value="withdrawnOfficerName"
            variant="outlined"
            density="compact"
            readonly
            hide-details
            class="readonly-field"
          ></v-text-field>
        </div>

        <!-- Position (해임되는 포지션) -->
        <div class="mb-4" style="max-width: 70%;">
          <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
            {{ t('startTask.Position') || 'Position' }} <span class="text-error">*</span>
          </label>
          <v-text-field
            :model-value="withdrawnPositionName || position || 'Director'"
            variant="outlined"
            density="compact"
            readonly
            hide-details
            class="readonly-field"
          ></v-text-field>
        </div>

        <!-- Date of Cessation (해임 날짜) -->
        <div class="mb-4" style="max-width: 70%;">
          <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
            {{ t('startTask.Date of Cessation') || 'Date of Cessation' }}
            <span v-if="!readOnly" class="text-error">*</span>
          </label>
          <v-menu
            v-if="!readOnly"
            v-model="dateOfCessationMenu"
            :close-on-content-click="false"
            location="bottom"
          >
            <template #activator="{ props: menuProps }">
              <v-text-field
                v-bind="menuProps"
                :model-value="dateOfCessationDisplay"
                :placeholder="datePlaceholderByJurisdiction"
                variant="outlined"
                density="compact"
                :error-messages="dateOfCessationErrorMessages"
                readonly
                prepend-inner-icon="mdi-calendar"
                data-field="dateOfCessation"
              ></v-text-field>
            </template>
            <v-locale-provider :locale="dateOfCessationField.datePickerLocale.value">
              <v-date-picker
                v-model="dateOfCessationField.datePickerValue.value"
                :first-day-of-week="dateOfCessationField.firstDayOfWeek.value"
                :max="todayDate"
                @update:model-value="dateOfCessationMenu = false"
              ></v-date-picker>
            </v-locale-provider>
          </v-menu>
          <v-text-field
            v-else
            :model-value="withdrawnDateOfCessationDisplay || dateOfCessationDisplay"
            variant="outlined"
            density="compact"
            readonly
            hide-details
            class="readonly-field"
          ></v-text-field>
        </div>

        <!-- Reason for Cessation (해임 이유) -->
        <div class="mb-4" style="max-width: 70%;">
          <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
            {{ t('startTask.Reason for Cessation') || 'Reason for Cessation' }}
            <span v-if="!readOnly" class="text-error">*</span>
          </label>
          <v-select
            v-if="!readOnly"
            v-model="reasonForCessation"
            :items="reasonOptions"
            :placeholder="t('startTask.Select') || 'Select'"
            variant="outlined"
            density="compact"
            :error-messages="reasonForCessationErrorMessages"
            :loading="isLoadingReasons"
            clearable
            data-field="reasonForCessation"
          ></v-select>
          <v-text-field
            v-else
            :model-value="withdrawnReasonDisplay || reasonForCessation"
            variant="outlined"
            density="compact"
            readonly
            hide-details
            class="readonly-field"
          ></v-text-field>
        </div>

        <!-- Other Reason (해임 이유가 Other일 때) -->
        <div
          v-if="(readOnly && withdrawnReasonCd === 'B12004' && withdrawnOtherReason) || (!readOnly && reasonForCessation === 'B12004')"
          class="mb-4"
          style="max-width: 70%;"
        >
          <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
            {{ t('startTask.Other Reasons') || 'Other Reasons' }}
          </label>
          <v-textarea
            v-if="!readOnly"
            v-model="otherReason"
            variant="outlined"
            density="compact"
            hide-details
            rows="4"
            :placeholder="t('startTask.Please enter other reasons') || 'Please enter other reasons'"
          ></v-textarea>
          <v-textarea
            v-else
            :model-value="withdrawnOtherReason || otherReason"
            variant="outlined"
            density="compact"
            readonly
            hide-details
            rows="4"
            class="readonly-field"
          ></v-textarea>
        </div>
      </div>

      <!-- 일반 모드: Entity Field -->
      <div v-else class="mb-6" style="max-width: 70%;" data-field="entityName" :doc_variable="'{#CompanyName}'">
        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
          {{ t('startTask.Entity') || 'Entity' }} <span class="text-error">*</span>
        </label>
        <v-autocomplete
          ref="entityInputRef"
          v-model="entity"
          :items="officerCessationStore.entityOptions"
          :placeholder="t('startTask.Search') || 'Search'"
          variant="outlined"
          density="compact"
          :error-messages="entityErrorMessages"
          append-inner-icon="mdi-magnify"
          clearable
        ></v-autocomplete>
      </div>

      <!-- Officer List (Accordion) - 일반 모드에서만 표시 -->
      <div v-if="!eFilingMode && entity" class="mb-6">
        <div class="text-subtitle-2 font-weight-medium mb-3">
          {{ t('startTask.Select the position holder from the list below') || 'Select the position holder from the list below and click "Withdraw" to remove them from the entity.' }}
        </div>
        
        <v-expansion-panels
          v-model="expandedPanels"
          multiple
          variant="accordion"
        >
          <v-expansion-panel
            v-for="(officer, index) in officers"
            :key="officer.contactCd"
            :value="index"
            :class="{ 'selected-officer': selectedOfficerIndex === index }"
          >
            <v-expansion-panel-title class="officer-accordion-header">
              <div class="d-flex align-center justify-space-between w-100">
                <div class="d-flex align-center">
                  <span class="font-weight-bold text-white mr-4">{{ index + 1 }}. {{ officer.contactName }}</span>
                  <span v-if="officer.positions && officer.positions.length > 0" class="text-white mr-2">
                    {{ officer.positions.map(p => p.positionName || p.positionCd).join(', ') }}
                  </span>
                  <span v-if="officer.idTypeCd" class="text-white mr-2">· {{ getIdTypeCodeName(officer.idTypeCd) }}</span>
                  <span v-if="officer.contactRegNo" class="text-white">· {{ officer.contactRegNo }}</span>
                </div>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text :data-officer-index="index">
              <!-- 기본 표시: Position 정보와 Withdraw 버튼 -->
              <div class="d-flex justify-space-between align-start mb-4">
                <div class="flex-grow-1">
                  <div class="text-body-1 font-weight-bold mb-3">
                    {{ t('startTask.Position(s) held') || 'Position(s) held' }}
                  </div>
                  <div v-if="officer.positions && officer.positions.length > 0">
                    <div
                      v-for="(pos, posIndex) in officer.positions"
                      :key="posIndex"
                      class="officer-position-item"
                    >
                      <div class="text-body-2">
                        <strong>{{ t('startTask.Position held') || 'Position held' }}:</strong> {{ pos.positionName || pos.positionCd }}
                        <span v-if="pos.appointmentDate"> · <strong>{{ t('startTask.Date of appointment') || 'Date of appointment' }}:</strong> {{ formatDateByJurisdiction(pos.appointmentDate) }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-body-2 text-medium-emphasis">
                    {{ t('startTask.No position information available') || 'No position information available' }}
                  </div>
                </div>
                <v-btn
                  v-if="!officerFormMode.get(index) && (withdrawnOfficerIndex === null || withdrawnOfficerIndex === index)"
                  color="primary"
                  variant="outlined"
                  size="small"
                  @click.stop="handleWithdraw(index)"
                  class="ml-4"
                >
                  <v-icon start>mdi-minus-circle</v-icon>
                  {{ t('startTask.Withdraw') || 'Withdraw' }}
                </v-btn>
              </div>
              
              <!-- 폼 모드: 해임 날짜/사유 입력 (Withdraw 버튼 클릭 시 표시) -->
              <div v-if="officerFormMode.get(index)">
                <!-- 직책 선택 셀렉트 (항상 표시) -->
                <div class="mb-4">
                  <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                    {{ t('startTask.Position') || 'Position' }}
                  </label>
                  <v-select
                    :model-value="(officerSelectedPosition.get(index) as string) || ''"
                    @update:model-value="(val: string) => updateOfficerSelectedPosition(index, val)"
                    @click="selectOfficer(index)"
                    :items="getOfficerPositionOptions(index)"
                    :placeholder="t('startTask.Please Select') || 'Please Select'"
                    variant="outlined"
                    density="compact"
                    :error-messages="withdrawnOfficerIndex === index ? positionErrorMessages : []"
                    clearable
                    data-field="position"
                    :data-officer-index="index"
                  ></v-select>
                </div>
                
                <div class="mb-4">
                  <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                    {{ t('startTask.Date of Cessation') || 'Date of Cessation' }} <span class="text-error">*</span>
                  </label>
                  <v-menu 
                    :model-value="officerDateMenuOpen.get(index) || false"
                    @update:model-value="(val: boolean) => officerDateMenuOpen.set(index, val)"
                    :close-on-content-click="false" 
                    location="bottom"
                  >
                    <template v-slot:activator="{ props: menuProps }">
                      <v-text-field
                        v-bind="menuProps"
                        :model-value="officerDateOfCessation.get(index) ? formatDateByJurisdiction(officerDateOfCessation.get(index) as string) : ''"
                        :placeholder="datePlaceholderByJurisdiction"
                        variant="outlined"
                        density="compact"
                        :error-messages="withdrawnOfficerIndex === index ? dateOfCessationErrorMessages : []"
                        readonly
                        prepend-inner-icon="mdi-calendar"
                        @click="selectOfficer(index)"
                        data-field="dateOfCessation"
                        :data-officer-index="index"
                      ></v-text-field>
                    </template>
                    <v-locale-provider :locale="dateOfCessationField.datePickerLocale.value">
                      <v-date-picker
                        :model-value="getOfficerDateValue(index)"
                        @update:model-value="(val: Date | null) => updateOfficerDateOfCessation(index, val)"
                        :first-day-of-week="dateOfCessationField.firstDayOfWeek.value"
                        :max="todayDate"
                      ></v-date-picker>
                    </v-locale-provider>
                  </v-menu>
                </div>
                <div class="mb-4">
                  <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                    {{ t('startTask.Reason for Cessation') || 'Reason for Cessation' }} <span class="text-error">*</span>
                  </label>
                  <v-select
                    :model-value="(officerReasonForCessation.get(index) as string) || ''"
                    @update:model-value="(val: string) => updateOfficerReasonForCessation(index, val)"
                    @click="selectOfficer(index)"
                    :items="reasonOptions"
                    :placeholder="t('startTask.Select') || 'Select'"
                    variant="outlined"
                    density="compact"
                    :error-messages="withdrawnOfficerIndex === index ? reasonForCessationErrorMessages : []"
                    :loading="isLoadingReasons"
                    clearable
                    data-field="reasonForCessation"
                    :data-officer-index="index"
                  ></v-select>
                  <div v-if="(officerReasonForCessation.get(index) as string) === 'B12004'" class="mt-4">
                    <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                      {{ t('startTask.Other Reasons') || 'Other Reasons' }}
                    </label>
                    <v-textarea
                      :model-value="(officerOtherReason.get(index) as string) || ''"
                      @update:model-value="(val: string) => updateOfficerOtherReason(index, val)"
                      @click="selectOfficer(index)"
                      variant="outlined"
                      density="compact"
                      hide-details
                      rows="4"
                      :placeholder="t('startTask.Please enter other reasons') || 'Please enter other reasons'"
                    ></v-textarea>
                  </div>
                </div>
                <div class="d-flex justify-start mt-4">
                  <v-btn
                    variant="outlined"
                    color="error"
                    size="small"
                    @click.stop="handleCancelOfficer(index)"
                  >
                    {{ t('startTask.Cancel') || 'Cancel' }}
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <div v-if="isLoadingOfficers" class="text-center py-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <div class="mt-2 text-body-2">{{ t('startTask.Loading officers') || 'Loading officers...' }}</div>
        </div>
        <div v-else-if="officers.length === 0 && entity" class="text-center py-4 text-body-2 text-medium-emphasis">
          {{ t('startTask.No officers found') || 'No officers found for this entity' }}
        </div>
      </div>

      <!-- Task Flow Section (doc template 바로 위에 표시) -->
      <TaskFlowSection
        v-if="!eFilingMode && withdrawnOfficerIndex !== null && officerSelectedPosition.get(withdrawnOfficerIndex)"
        v-model="taskFlow"
        :visible="true"
        :is-all-supported="isAllSupportedFinal"
        :is-create-document-supported="isCreateDocumentSupported"
        :is-request-t-sign-supported="isRequestTSignSupported"
        :is-proceed-with-e-filing-supported="isEFilingSupportedFinal"
        class="mb-6 mt-6"
      />

    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
/* 폰트 패밀리 - Inter */
* {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* 카드 타이틀 - 18px Bold */
:deep(.v-card-title) {
  font-size: 18px !important;
  font-weight: 700 !important;
  color: #3C4650;
}

/* 레이블 - 14px */
:deep(.v-label),
:deep(.v-field-label) {
  font-size: 14px !important;
  color: #3C4650;
}

/* 입력 필드 - 14px */
:deep(.v-field__input) {
  font-size: 14px !important;
  color: #3C4650;
}

/* placeholder - 14px #777E85 */
:deep(.v-field__input::placeholder),
:deep(input::placeholder) {
  font-size: 14px !important;
  color: #777E85 !important;
}

/* 버튼 - 14px */
:deep(.v-btn) {
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: none;
}

/* Primary 버튼 */
:deep(.v-btn--color-primary) {
  background-color: #1B84FF !important;
}

.text-error {
  color: rgb(var(--v-theme-error));
}

/* 선택된 Officer 하이라이트 */
.selected-officer {
  border: 2px solid rgba(27, 132, 255, 0.5) !important;
}

/* Accordion 헤더 (Primary 색상 - SAVE 버튼과 동일) */
.officer-accordion-header {
  background-color: #1B84FF !important;
  color: white !important;
  min-height: 60px !important;
}

.officer-accordion-header :deep(.v-expansion-panel-title__overlay) {
  background-color: #1B84FF !important;
}

.officer-accordion-header :deep(.v-expansion-panel-title__icon) {
  color: white !important;
}

/* Accordion 패널 스타일 */
:deep(.v-expansion-panel-title) {
  font-size: 14px !important;
  min-height: 48px;
  padding: 12px 16px;
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: 16px;
}

/* Accordion 항목 간 간격 추가 */
:deep(.v-expansion-panels .v-expansion-panel) {
  margin-bottom: 12px;
}

:deep(.v-expansion-panels .v-expansion-panel:last-child) {
  margin-bottom: 0;
}

/* Accordion 패널 내용 스타일 */
.officer-position-item {
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.officer-position-item:last-child {
  border-bottom: none;
}

/* Readonly 필드 스타일 */
.readonly-field {
  pointer-events: none;
}

.readonly-field :deep(.v-field) {
  background-color: white !important;
  opacity: 1 !important;
}

.readonly-field :deep(.v-field:hover) {
  background-color: white !important;
}

.readonly-field :deep(.v-field__input) {
  color: rgba(0, 0, 0, 0.87) !important;
  -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
  background-color: white !important;
}

.readonly-field :deep(.v-field--variant-outlined) {
  background-color: white !important;
}

.readonly-field :deep(.v-field--variant-outlined:hover) {
  background-color: white !important;
}

.readonly-field :deep(.v-field__overlay) {
  background-color: transparent !important;
  opacity: 0 !important;
}

.readonly-field :deep(input),
.readonly-field :deep(textarea) {
  cursor: default !important;
  background-color: white !important;
}
</style>

